/**
 * VoiceInput — mic toggle using the browser Web Speech API.
 *
 * Electron's renderer runs Chromium, which exposes SpeechRecognition backed
 * by the OS speech engine (Windows SAPI / macOS Dictation). No model download,
 * no IPC, no main-process involvement.
 *
 * Why this instead of @xenova/transformers Whisper:
 *   - Zero bundle size impact (no 250 MB ONNX model)
 *   - Uses whatever microphone/headset the user has configured at OS level
 *   - Compatible with WisprFlow and other OS-level dictation tools running
 *     alongside CipherOps — both can coexist; this button is a fallback for
 *     users who don't have a system-level dictation tool set up
 *
 * SEC-04: First mic click shows a one-time privacy notice that the OS speech
 * engine may send audio to Microsoft/Apple cloud servers. The user must
 * acknowledge before recording starts. Acknowledgment is stored in a session
 * flag (resets on app restart — appropriate given the sensitivity of audit data).
 *
 * Usage:
 *   <VoiceInput
 *     onTranscript={(text) => setMyField((prev) => prev + ' ' + text)}
 *   />
 *
 * Availability:
 *   SpeechRecognition is available in Electron ≥ 3 (Chromium ≥ 64) on
 *   Windows and macOS. On Linux it may require an online service — the
 *   component surfaces a "not supported" state gracefully.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

// Electron exposes both prefixed and unprefixed variants
const SR =
  typeof window !== 'undefined'
    ? (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
    : null;

// SEC-04: session-scoped acknowledgment flag (in-memory only — resets on restart).
// We intentionally do NOT persist this to localStorage: pentest data is sensitive
// and each session should re-confirm the privacy trade-off.
let voicePrivacyAcknowledged = false;

export interface VoiceInputProps {
  /** Called with the final transcript text when recognition ends. */
  onTranscript: (text: string) => void;
  /** Disable the button (e.g. while parent form is submitting). */
  disabled?: boolean;
  style?: React.CSSProperties;
}

// ---------------------------------------------------------------------------
// VoicePrivacyModal — shown once per session before the first recording
// ---------------------------------------------------------------------------

interface PrivacyModalProps {
  onAcknowledge: () => void;
  onCancel: () => void;
}

function VoicePrivacyModal({ onAcknowledge, onCancel }: PrivacyModalProps) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26,31,46,0.72)',
        zIndex: 9000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface, #fff)',
          border: '0.5px solid var(--border, #d0ccc4)',
          padding: '32px 36px',
          maxWidth: 480,
          width: '90%',
          fontFamily: 'var(--sans, system-ui, sans-serif)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
          <span style={{ fontSize: 24 }}>🎙️</span>
          <div>
            <div style={{
              fontFamily: 'var(--serif, Georgia, serif)',
              fontSize: 16,
              color: 'var(--text, #1a1f2e)',
              marginBottom: 4,
            }}>
              Voice Input — Privacy Notice
            </div>
            <div style={{
              fontFamily: 'var(--mono, "Courier New", monospace)',
              fontSize: 10,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: 'var(--text-muted, #7a7d8a)',
            }}>
              Read before enabling
            </div>
          </div>
        </div>

        {/* Body */}
        <p style={{
          fontSize: 13,
          lineHeight: 1.7,
          color: 'var(--text, #1a1f2e)',
          marginBottom: 16,
        }}>
          Voice Input uses your operating system's built-in speech engine
          (Windows Speech Recognition / macOS Dictation). Depending on your OS
          settings, <strong>your audio may be sent to Microsoft or Apple cloud
          servers</strong> for transcription.
        </p>
        <p style={{
          fontSize: 13,
          lineHeight: 1.7,
          color: 'var(--text, #1a1f2e)',
          marginBottom: 24,
        }}>
          If your engagement is under strict data-handling obligations, verify
          that OS-level cloud transcription is disabled before using this
          feature. You can use WisprFlow or other offline dictation tools as an
          alternative — just dictate directly into any text field.
        </p>

        {/* Note */}
        <div style={{
          background: 'var(--surface-alt, #eae8e2)',
          borderLeft: '3px solid var(--accent, #8b1a2e)',
          padding: '10px 14px',
          fontSize: 12,
          color: 'var(--text-muted, #7a7d8a)',
          fontFamily: 'var(--serif, Georgia, serif)',
          fontStyle: 'italic',
          marginBottom: 24,
        }}>
          This notice is shown once per session. It resets each time you restart CipherOps.
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '8px 20px',
              fontFamily: 'var(--serif, Georgia, serif)',
              fontSize: 13,
              background: 'transparent',
              border: '1px solid var(--border, #d0ccc4)',
              color: 'var(--text-muted, #7a7d8a)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onAcknowledge}
            style={{
              padding: '8px 20px',
              fontFamily: 'var(--serif, Georgia, serif)',
              fontSize: 13,
              background: 'var(--brand, #1a1f2e)',
              border: '1px solid var(--brand, #1a1f2e)',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            I Understand — Enable Voice
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VoiceInput component
// ---------------------------------------------------------------------------

export default function VoiceInput({ onTranscript, disabled, style }: VoiceInputProps) {
  const [recording,     setRecording]     = useState(false);
  const [partial,       setPartial]       = useState('');
  const [error,         setError]         = useState('');
  const [showPrivacy,   setShowPrivacy]   = useState(false);
  const recognitionRef = useRef<any>(null);

  // Graceful degradation — component is invisible when API is unavailable
  if (!SR) return null;

  const stopRecognition = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setRecording(false);
    setPartial('');
  }, []);

  const startRecognition = useCallback(() => {
    setError('');
    setPartial('');

    const rec = new SR();
    rec.continuous      = false;  // single utterance; re-trigger to keep going
    rec.interimResults  = true;   // stream partial results for the live preview
    rec.lang            = 'en-US';

    rec.onstart = () => setRecording(true);

    rec.onresult = (event: any) => {
      let interim = '';
      let final   = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += t;
        else interim += t;
      }
      if (interim) setPartial(interim);
      if (final)   setPartial(final);   // show final before it fires onend
    };

    rec.onend = () => {
      // Grab last partial as the final transcript if we have one
      setPartial((prev) => {
        if (prev.trim()) onTranscript(prev.trim());
        return '';
      });
      setRecording(false);
      recognitionRef.current = null;
    };

    rec.onerror = (event: any) => {
      const msg: Record<string, string> = {
        'no-speech':         'No speech detected — try again',
        'audio-capture':     'Microphone not available',
        'not-allowed':       'Microphone permission denied',
        'network':           'Network error (check connection)',
        'aborted':           '',  // user cancelled — not an error
      };
      const friendly = msg[event.error] ?? `Speech error: ${event.error}`;
      if (friendly) setError(friendly);
      setRecording(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = rec;
    rec.start();
  }, [onTranscript]);

  // SEC-04: Gate on privacy acknowledgment before starting
  const handleMicClick = useCallback(() => {
    if (recording) {
      stopRecognition();
      return;
    }
    if (!voicePrivacyAcknowledged) {
      setShowPrivacy(true);
    } else {
      startRecognition();
    }
  }, [recording, stopRecognition, startRecognition]);

  const handlePrivacyAck = useCallback(() => {
    voicePrivacyAcknowledged = true;
    setShowPrivacy(false);
    startRecognition();
  }, [startRecognition]);

  const handlePrivacyCancel = useCallback(() => {
    setShowPrivacy(false);
  }, []);

  // Clean up on unmount
  useEffect(() => () => { recognitionRef.current?.abort(); }, []);

  return (
    <>
      {/* SEC-04: One-time privacy notice modal */}
      {showPrivacy && (
        <VoicePrivacyModal
          onAcknowledge={handlePrivacyAck}
          onCancel={handlePrivacyCancel}
        />
      )}

      <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, ...style }}>
        <button
          type="button"
          title={recording ? 'Stop recording — transcribe' : 'Start voice input (or use WisprFlow / OS dictation in any field)'}
          disabled={disabled}
          onClick={handleMicClick}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            fontFamily: 'var(--serif)',
            fontSize: 12,
            background: recording ? 'var(--accent)' : 'transparent',
            color: recording ? '#fff' : 'var(--text-muted)',
            border: `1px solid ${recording ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius: 3,
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s, color 0.15s, border-color 0.15s',
            opacity: disabled ? 0.55 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {/* Recording indicator dot */}
          <span
            style={{
              display: 'inline-block',
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: recording ? '#fff' : 'var(--text-muted)',
              animation: recording ? 'voice-pulse 1s ease-in-out infinite' : 'none',
              flexShrink: 0,
            }}
          />
          {recording ? 'Stop Recording' : 'Voice Input'}
        </button>

        {/* Live partial preview */}
        {partial && (
          <div
            style={{
              fontSize: 11,
              fontFamily: 'var(--serif)',
              fontStyle: 'italic',
              color: 'var(--text-muted)',
              paddingLeft: 8,
              borderLeft: '2px solid var(--accent)',
              maxWidth: 380,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {partial}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ fontSize: 11, color: 'var(--sev-high)', fontFamily: 'var(--mono)' }}>
            ✗ {error}
          </div>
        )}
      </div>
    </>
  );
}
